import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PortalPrimitive } from '#pplx/frontend/aether/primitives/PortalPrimitive/index.js';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { voiceModeInterfaceActionEvent } from '#pplx/frontend/apps/ask/events/voice/analytics.js';
import { useWebVoiceRequest } from '#pplx/frontend/apps/ask/multimodal/voice/launcher/useWebVoiceRequest.js';
import { WebVoiceContext, useWebVoiceContext, } from '#pplx/frontend/apps/ask/multimodal/voice/launcher/WebVoiceContext.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useBooleanFlagValue } from '#pplx/frontend/experimentation/hooks.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
const WebVoiceOverlay = Dynamic(async () => {
    const { WebVoiceOverlay } = await import('#pplx/frontend/apps/ask/multimodal/voice/web-session/WebVoiceOverlay.js');
    return { default: WebVoiceOverlay };
}, {
    error: WebVoiceLoadError,
});
function WebVoiceLoadError() {
    const { $t } = useIntl();
    const { openToast } = useToastContext();
    const controls = useWebVoiceContext();
    const handled = useRef(false);
    useEffect(() => {
        if (!controls?.isActive) {
            handled.current = false;
            return;
        }
        if (handled.current)
            return;
        handled.current = true;
        openToast({
            message: $t({
                defaultMessage: 'Could not start voice',
                id: '65cc4fd3e5',
                description: 'Voice bundle loading failure status',
            }),
            variant: 'error',
            timeoutSeconds: 5,
        });
        controls.close();
    }, [controls, openToast, $t]);
    return null;
}
export function WebVoiceProvider({ children }) {
    const { track } = useInstrumentation();
    const activeSession = useRef(null);
    const { session } = useSession();
    const accountId = session?.user?.id;
    const pathname = usePathname() ?? '/';
    const hasDedicatedVoice = /^\/(speak|voice)(\/|$)/.test(pathname);
    const { device: { isMobile, isWindowsApp }, } = useWebPlatform();
    const { variation, loading } = useBooleanFlagValue({
        flag: 'multimodal-web-openai-live-voice-enabled',
        defaultValue: false,
    });
    const enabled = !import.meta.env.VITE_IS_SIDECAR_BUILD &&
        !import.meta.env.VITE_IS_VOICE_ASSISTANT_BUILD &&
        !isMobile &&
        !isWindowsApp &&
        !hasDedicatedVoice &&
        !!accountId &&
        !loading &&
        variation;
    const { request, open: requestOpen, close: requestClose, } = useWebVoiceRequest({ eligible: enabled, accountId });
    const returnFocus = useRef(null);
    const open = useCallback(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && !active.closest('[data-web-voice-panel]'))
            returnFocus.current = active;
        requestOpen();
    }, [requestOpen]);
    const close = useCallback(() => {
        const restore = document.activeElement?.closest('[data-web-voice-panel]');
        requestClose();
        if (restore && returnFocus.current?.isConnected)
            returnFocus.current.focus();
    }, [requestClose]);
    const stop = useCallback(() => {
        if (!request)
            return;
        track(voiceModeInterfaceActionEvent({
            name: 'voice mode interface action',
            data: { action: 'session stopped' },
        }));
        if (activeSession.current)
            activeSession.current.stop();
        else
            close();
    }, [request, track, close]);
    const isActive = request !== null;
    const controls = useMemo(() => (enabled ? { open, close: stop, isActive } : null), [enabled, open, stop, isActive]);
    return (_jsxs(WebVoiceContext.Provider, { value: controls, children: [children, enabled && request && (_jsx(PortalPrimitive, { children: _jsx(WebVoiceOverlay, { ref: activeSession, focusRequest: request.focusRequest, onClose: close }, accountId) }))] }));
}
//# sourceMappingURL=WebVoiceProvider.js.map